import watermarkSvg from '../assets/livinpp-logo-watermark.svg';

export type WatermarkPosition =
  | 'center'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right';

export type WatermarkSize = 'small' | 'medium' | 'large' | 'xlarge';

export interface WatermarkRequest {
  src: string;
  watermarkOpacity?: number;
  watermarkPosition?: WatermarkPosition;
  watermarkSize?: WatermarkSize;
}

export interface PreparedWatermarkImage {
  sourceSrc: string;
  displaySrc: string;
  needsOverlay: boolean;
}

const preparedCache = new Map<string, PreparedWatermarkImage>();
const pendingCache = new Map<string, Promise<PreparedWatermarkImage>>();

let watermarkImagePromise: Promise<HTMLImageElement> | null = null;

const getWatermarkSizeRatio = (watermarkSize: WatermarkSize) => {
  switch (watermarkSize) {
    case 'small':
      return 0.4;
    case 'medium':
      return 0.5;
    case 'xlarge':
      return 0.65;
    case 'large':
    default:
      return 0.6;
  }
};

const loadImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

const getWatermarkImage = () => {
  if (!watermarkImagePromise) {
    watermarkImagePromise = loadImage(watermarkSvg);
  }
  return watermarkImagePromise;
};

const resolveSourceSrc = (src: string) => {
  const apiBase = process.env.REACT_APP_API_BASE || '';
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const isAbsoluteHttp = src.startsWith('http://') || src.startsWith('https://');
  const needsProxy = Boolean(isAbsoluteHttp && currentOrigin && !src.startsWith(currentOrigin));

  if (needsProxy && apiBase) {
    return `${apiBase.replace(/\/$/, '')}/image-proxy?url=${encodeURIComponent(src)}`;
  }

  return src;
};

const createFallbackResult = (sourceSrc: string): PreparedWatermarkImage => ({
  sourceSrc,
  displaySrc: sourceSrc,
  needsOverlay: true,
});

export const prepareWatermarkedImage = async ({
  src,
  watermarkOpacity = 0.5,
  watermarkPosition = 'center',
  watermarkSize = 'large',
}: WatermarkRequest): Promise<PreparedWatermarkImage> => {
  if (!src) {
    return {
      sourceSrc: '',
      displaySrc: '',
      needsOverlay: false,
    };
  }

  const sourceSrc = resolveSourceSrc(src);
  const cacheKey = `${sourceSrc}|${watermarkOpacity}|${watermarkPosition}|${watermarkSize}`;

  const cached = preparedCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const pending = pendingCache.get(cacheKey);
  if (pending) {
    return pending;
  }

  const promise = (async () => {
    try {
      const [sourceImage, watermarkImage] = await Promise.all([
        loadImage(sourceSrc),
        getWatermarkImage(),
      ]);

      const canvas = document.createElement('canvas');
      canvas.width = sourceImage.naturalWidth;
      canvas.height = sourceImage.naturalHeight;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return createFallbackResult(sourceSrc);
      }

      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(sourceImage, 0, 0);

      const watermarkWidth = canvas.width * getWatermarkSizeRatio(watermarkSize);
      const watermarkHeight = watermarkWidth / (watermarkImage.width / watermarkImage.height);

      let x = canvas.width / 2;
      let y = canvas.height / 2;

      switch (watermarkPosition) {
        case 'top-left':
          x = canvas.width * 0.1 + watermarkWidth / 2;
          y = canvas.height * 0.1 + watermarkHeight / 2;
          break;
        case 'top-right':
          x = canvas.width * 0.9 - watermarkWidth / 2;
          y = canvas.height * 0.1 + watermarkHeight / 2;
          break;
        case 'bottom-left':
          x = canvas.width * 0.1 + watermarkWidth / 2;
          y = canvas.height * 0.9 - watermarkHeight / 2;
          break;
        case 'bottom-right':
          x = canvas.width * 0.9 - watermarkWidth / 2;
          y = canvas.height * 0.9 - watermarkHeight / 2;
          break;
        default:
          break;
      }

      ctx.globalAlpha = watermarkOpacity;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(
        watermarkImage,
        x - watermarkWidth / 2,
        y - watermarkHeight / 2,
        watermarkWidth,
        watermarkHeight
      );
      ctx.globalAlpha = 1;
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      const prepared = {
        sourceSrc,
        displaySrc: canvas.toDataURL('image/jpeg', 0.92),
        needsOverlay: false,
      };
      preparedCache.set(cacheKey, prepared);
      return prepared;
    } catch (error) {
      const prepared = createFallbackResult(sourceSrc);
      preparedCache.set(cacheKey, prepared);
      return prepared;
    } finally {
      pendingCache.delete(cacheKey);
    }
  })();

  pendingCache.set(cacheKey, promise);
  return promise;
};
